import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import faqMap from "../../assets/faq/faq-map.png";
import formula from "../../assets/faq/formula.png";

const FAQComponent = () => {
  const faqData = [
    {
      question:
        "What is the main purpose of the drought risk financing dashboard and who can benefit from its insights?",
      answer: (
        <Typography>
          The main purpose of the online dashboard is to offer a user-friendly
          way to access satellite data to understand drought risk as well as to
          develop and validate parametric drought risk financing indices. It is
          intended to aid understanding and reduction of basis risk by making
          the development process more transparent. The dashboard provides
          access to pre-processed satellite-derived climate and drought loss
          data, the opportunity to tweak insurance parameters, and
          visualizations of simulated historical payouts.
        </Typography>
      ),
    },
    {
      question: "What is basis risk? ",
      answer: (
        <Typography>
          The main purpose of the online dashboard is to offer a user-friendly
          way to access satellite data to understand drought risk as well as to
          develop and validate parametric drought risk financing indices. It is
          intended to aid understanding and reduction of basis risk by making
          the development process more transparent. The dashboard provides
          access to pre-processed satellite-derived climate and drought loss
          data, the opportunity to tweak insurance parameters, and
          visualizations of simulated historical payouts.
        </Typography>
      ),
    },
    {
      question: "Which regions does the dashboard cover?",
      answer: (
        <Typography>
          The dashboard was originally developed to support drought index design
          in <b>Ethiopia, Kenya, and Somalia</b>, where early versions of the
          platform focused on enhancing drought risk analytics for the Horn of
          Africa. Today, the system has expanded to offer{" "}
          <b>full global coverage</b>, enabling users to perform drought index
          exploration and design in any region worldwide.
        </Typography>
      ),
    },
    {
      question:
        "How were the climate datasets selected and which periods to they cover?  ",
      answer: (
        <Box>
          <Typography>
            The three climate datasets (satellite-derived precipitation,
            root-zone soil moisture, vegetation health) were mainly chosen based
            on five factors: 1) complementarity 2) data quality and available
            validation studies 3) temporal coverage (at least 15 years) 4)
            latency, and 5) high likelihood of long-term data delivery. More
            detailed information can be found in the dashboard’s full
            documentation.{" "}
          </Typography>
          <br />
          <Typography>Temporal coverage:</Typography>
          <br />
          <Typography>
            CHIRPS Rainfall: 1981-present (merged CHIRPS and CHIRPS prelim){" "}
          </Typography>
          <Typography>
            Copernicus Soil Water Index (SWI): 2007-present{" "}
          </Typography>
          <Typography>
            eVIIRS Vegetation Greenness (Normalized Difference Vegetation Index;
            NDVI): 2002-present (merged with MODIS NDVI){" "}
          </Typography>
        </Box>
      ),
    },
    {
      question:
        "How is the CHIRPS prelim product different from the standard CHIRPS dataset? ",
      answer: (
        <Box>
          <Typography>
            The final CHIRPS rainfall product has a latency of 1.5 months – i.e.
            data is not available until 1.5 months after the satellite
            observation - and includes data from a higher number of weather
            stations than the prelim (= preliminary) product. Both products were
            merged, resulting in a longer temporal reference (CHIRPS prelim is
            only available since 2015). However, working with a merged product
            means that the last 1.5 months of rainfall data might be subject to
            quality issues in regions where the input from weather stations has
            a strong influence to correct over- or underestimations of the
            satellite sensor. You can check the difference between the two
            datasets for every month here:{" "}
            <a
              href="https://wiki.chc.ucsb.edu/CHIRPS_Reality_Checks"
              target="_blank"
            >
              https://wiki.chc.ucsb.edu/CHIRPS_Reality_Checks
            </a>
          </Typography>
        </Box>
      ),
    },
    {
      question:
        "Are there known regions in which any of the climate datasets show high levels of uncertainty?  ",
      answer: (
        <Box>
          <Typography>
            Yes. The level of uncertainty in climate and vegetation datasets
            varies globally and is influenced by factors such as complex
            terrain, dense tropical forests, persistent cloud cover, snow
            covered areas, and arid or sparsely vegetated regions. These
            conditions can reduce the accuracy of satellite derived products.
          </Typography>
          <br />
          <Typography>
            Regions classified as <b>Tier 2 or Tier 3</b> generally present
            higher uncertainty due to limited ground observations, challenging
            surface characteristics, or greater variability in the underlying
            climate signals. Users should be particularly cautious when
            interpreting indicators in areas labelled as{" "}
            <b>bare land, sparse vegetation, or mountainous terrain</b>, as
            errors in the underlying datasets can propagate through all
            processing steps and affect the final financial assessment.
          </Typography>
          <br />
          <Typography>
            The latest (Jan 2026) version of the NGDI platform includes a basic
            global mask to exclude pixels representing permanently frozen soils
            or bare land/deserts. However, other factors increasing uncertainty,
            such as dense tropical forests or complex topography, still persist.
            Please reach out if you have questions about the performance of a
            specific climate variable in your target region.
          </Typography>
          <br />
          <Box textAlign="center" bgcolor="white">
            <img width="100%" alt="faqMap" src={faqMap} />
          </Box>
        </Box>
      ),
    },
    {
      question:
        "What does the concept of ‘convergence of evidence’ mean for drought risk financing?   ",
      answer: (
        <Typography>
          Convergence of evidence means that several independent data confirm
          the same narrative. Hence, independent observations are a
          prerequisite. All climate data used in the dashboard are derived from
          different satellites, using different sensor technologies and
          algorithms. More concretely, this means that soil moisture responds to
          rainfall deficits and vegetation greenness (a proxy for vegetation
          health) responds to soil moisture deficits.
        </Typography>
      ),
    },
    {
      question: "What is a climate anomaly and how is it calculated? ",
      answer: (
        <Box>
          <Typography>
            There are{" "}
            <a
              href="https://www.droughtmanagement.info/literature/GWP_Handbook_of_Drought_Indicators_and_Indices_2016.pdf"
              target="_blank"
            >
              more than 50 different drought indices of indicators
            </a>
            , ranging from very simple to complex formulas. We rely on 10-daily
            standardized anomalies (Z-scores) for all climate and vegetation
            variables.
          </Typography>
          <br />
          <Box textAlign="center">
            <img height="150px" alt="formula" src={formula} />
          </Box>
        </Box>
      ),
    },
    {
      question: "What is a land-cover mask, and why should I use it?",
      answer: (
        <Typography>
          A land-cover mask is a filter that excludes areas where drought
          indicators are not meaningful (e.g. forests, urban areas, permanent
          water, snow/ice). Depending on the selected variable (precipitation,
          soil moisture, or vegetation stress), data are processed only over
          land-cover classes that are relevant for drought index design. This
          reduces noise and false signals, ensuring that drought metrics and
          payouts reflect conditions in the relevant exposure areas.
        </Typography>
      ),
    },
    {
      question:
        "At what geographic scale can climate data be aggregated, and indices developed?",
      answer: (
        <Typography>
          Climate data in the dashboard can be aggregated at Administrative
          Level 1 (ADM1) and Administrative Level 2 (ADM2), and users can also
          develop drought indices for any custom geographic area by uploading
          their own shapefiles. This means indices can be generated at
          district/county level worldwide or at any user defined polygon.
        </Typography>
      ),
    },
    {
      question:
        "Which datasets are used to represent historical drought impacts/losses? ",
      answer: (
        <Typography>
          We use two datasets to represent drought impacts: drought statistics
          from the{" "}
          <a href="http://www.emdate.be/" target="_blank">
            International Disasters Database
          </a>{" "}
          and the{" "}
          <a
            href="https://fews.net/about/integrated-phase-classification"
            target="_blank"
          >
            Integrated Food Security Phase Classification
          </a>{" "}
          (IPC) scale for Acute Food Insecurity. IPC scores are generated at the
          level of FEWS NET livelihood zones (higher resolution than Admin 2),
          but we use the maximum, annual IPC value for the chosen Admin level.
        </Typography>
      ),
    },
    {
      question:
        "How are trigger and exit values defined and can I change them? ",
      answer: (
        <Typography>
          Initial trigger and exit values are auto-selected based on standard
          return periods (1 in 5, 1 in 20 years), but can always be changed.
        </Typography>
      ),
    },
    {
      question:
        "How are drought conditions related to socioeconomic impacts, such as crop failure or food insecurity? ",
      answer: (
        <Box>
          <Typography>
            Agricultural droughts can cause crop failure, which often leads to a
            'hunger gap' – a delay between the drought onset and resultant food
            insecurity, as dwindling food supplies and rising prices gradually
            impact communities. This means that there might be many months
            between climate variables indicating drought conditions and food
            security increasing.
          </Typography>
          <br />
          <Typography>
            In addition, drought shocks can disrupt economic development and
            jeopardize all kinds of development gains. Exposure to early
            childhood droughts leads to lower adult wealth, reduced education,
            and shorter adult stature in women raised in rural households,
            indicating the long-term socioeconomic impacts of drought-related
            crop loss (see for instance Hyland and Russ, 2019).
          </Typography>
        </Box>
      ),
    },
    {
      question:
        "How can I validate historical insurance payouts with my own data? ",
      answer: (
        <Box>
          <Typography>
            We strongly encourage all kinds of validation exercises,
            particularly if you have trusted loss data available for the target
            regions. You can download the data via csv (results only)
          </Typography>
        </Box>
      ),
    },
    {
      question: "Can I select more than one critical window? ",
      answer: (
        <Box>
          <Typography>
            <b>Yes.</b> The dashboard allows you to define{" "}
            <b>critical windows</b> (called Season Windows) within the same
            analysis. This is especially useful in regions with{" "}
            <b>bimodal rainfall patterns</b> or multiple growing seasons. You
            can set a separate start and end date for each window, compare
            drought indices across them, and analyze how each season behaves
            independently. Two different windows also allow you to understand
            the time lag between different climate variables. For instance, you
            can design a rainfall-based index focusing on June and compare it
            with a soil moisture, a variable that relies on rainfall, in July.
          </Typography>
        </Box>
      ),
    },
    {
      question:
        "Can I compare historical payouts based on different climate variables and for different geographic units? ",
      answer: (
        <Box>
          <Typography>Yes.</Typography>
        </Box>
      ),
    },
    {
      question: "Is it possible to define my own area of interest?",
      answer: (
        <Box>
          <Typography>
            Yes. The dashboard allows users to upload their own shapefile to
            define a custom area of interest. This option becomes available once
            you are logged in.
          </Typography>
          <br />
          <Typography component="div">
            Please note that:
            <ul>
              <li>
                Only areas smaller than a country can be uploaded. Please note
                that uploaded shapefiles need to cover subnational areas within
                one country. Shapefiles covering multiple countries are not yet
                supported.
              </li>
              <li>
                Uploaded areas are private and will only be visible to your own
                user account.
              </li>
            </ul>
          </Typography>
        </Box>
      ),
    },
    {
      question: "Who can I contact if I have questions? ",
      answer: (
        <Box>
          <Typography>
            Please contact John Luke Plevin (
            <a href={`mailto:jplevin@worldbank.org`}>jplevin@worldbank.org</a>)
            or Markus Enenkel (
            <a href={`mailto:menenkel@worldbank.org`}>menenkel@worldbank.org</a>
            ).
          </Typography>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {faqData.map((faq, index) => (
        <Accordion key={index} sx={{ width: "100%" }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`faq-${index}-content`}
            id={`faq-${index}-header`}
          >
            <Typography variant="h6" fontSize="16px">
              {faq.question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>{faq.answer}</AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default FAQComponent;
